import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Menu, MenuItem, Stack, CircularProgress,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import { StandardScopeType } from '../productionTypes';
import { resolveStandard, updateScheduleEntry, upsertStandard } from '../productionApi';
import { GanttEntry } from './ganttTypes';
import { TOKENS } from './ganttConfig';

interface Props {
  entry: GanttEntry | null;
  open: boolean;
  onClose: () => void;
}

const SCOPE_LABELS: Record<StandardScopeType, string> = {
  forme: 'Forme', stock_code: 'Stock', substrate: 'Substrate', customer: 'Customer', default: 'Default',
};

const intOrZero = (v: string): number => {
  const n = parseInt(v, 10);
  return isNaN(n) ? 0 : n;
};

/**
 * SpeedReviewDialog — board-native inline review/edit of a scheduled job's speed
 * standard. Opened from a Gantt bar's action menu. Shows which standard governs
 * the job, then lets the operator apply a one-off override to this job or save a
 * reusable standard scoped to the forme, customer, or paper.
 */
export default function SpeedReviewDialog({ entry, open, onClose }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ setup: '', runRate: '', washup: '' });
  const [scopeAnchor, setScopeAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const standardQ = useQuery({
    queryKey: ['production', 'standards', 'resolve', entry?.id ?? 0],
    queryFn: () => resolveStandard({
      press: entry!.press,
      stock: entry!.stockCode,
      material: entry!.materialDesc,
      forme: entry!.forme,
      customer: entry!.cust || null,
    }),
    enabled: !!entry && open,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => { setMsg(null); }, [entry?.id]);

  useEffect(() => {
    const s = standardQ.data;
    if (s) setForm({ setup: String(s.setup_minutes), runRate: String(s.run_rate_per_hour), washup: String(s.washup_minutes) });
  }, [standardQ.data]);

  if (!entry) return <Dialog open={false} onClose={onClose} />;

  const std = standardQ.data;
  const governedBy = std
    ? std.scope_type === 'default' ? `Default (${std.press_code})` : `${SCOPE_LABELS[std.scope_type]} ${std.scope_value}`
    : 'No standard found';

  // Run minutes derive from the job's sheet count — mirrors the backend's
  // calculate_planned_parts (ceil(sheets / rate * 60)).
  const plannedFromSpeed = () => {
    const runRate = intOrZero(form.runRate);
    const run = runRate > 0 ? Math.ceil((entry.sheets / runRate) * 60) : 0;
    return { planned_setup_min: intOrZero(form.setup), planned_run_min: run, planned_washup_min: intOrZero(form.washup) };
  };

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['production', 'schedule'] });
    await qc.invalidateQueries({ queryKey: ['production', 'standards'] });
  };

  const applyOneOff = async () => {
    setSaving(true); setMsg(null);
    try {
      await updateScheduleEntry(entry.id, plannedFromSpeed());
      await refresh();
      setMsg('Applied to this job only.');
    } catch (e: any) {
      setMsg(e?.response?.data?.error || e.message || 'Failed to apply');
    } finally { setSaving(false); }
  };

  const savePermanent = async (scopeType: StandardScopeType, scopeValue: string | null) => {
    setScopeAnchor(null); setSaving(true); setMsg(null);
    try {
      await upsertStandard({
        press_code: entry.press,
        scope_type: scopeType,
        scope_value: scopeValue,
        setup_minutes: intOrZero(form.setup),
        run_rate_per_hour: intOrZero(form.runRate),
        washup_minutes: intOrZero(form.washup),
      });
      await updateScheduleEntry(entry.id, plannedFromSpeed());
      await refresh();
      setMsg('Saved as a standard and applied to this job.');
    } catch (e: any) {
      setMsg(e?.response?.data?.error || e.message || 'Failed to save standard');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <SpeedIcon fontSize="small" /> Speed assumption
        <Box component="span" sx={{ ml: 1, fontFamily: TOKENS.monoFont, fontWeight: 700, color: TOKENS.ink2 }}>
          {entry.job}
        </Box>
        <Box component="span" sx={{ fontWeight: 400, color: TOKENS.ink3, fontSize: 13 }}>on {entry.press}</Box>
      </DialogTitle>
      <DialogContent dividers>
        {standardQ.isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Governed by <strong>{governedBy}</strong>. Adjust the figures, then apply to this job only
              or save as a reusable standard.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Make-ready (min)" size="small" type="number" fullWidth value={form.setup}
                disabled={saving} onChange={(e) => setForm((p) => ({ ...p, setup: e.target.value }))} />
              <TextField label="Run rate (sheets/h)" size="small" type="number" fullWidth value={form.runRate}
                disabled={saving} onChange={(e) => setForm((p) => ({ ...p, runRate: e.target.value }))} />
              <TextField label="Wash-up (min)" size="small" type="number" fullWidth value={form.washup}
                disabled={saving} onChange={(e) => setForm((p) => ({ ...p, washup: e.target.value }))} />
            </Stack>
            {msg && <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>{msg}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={applyOneOff} disabled={saving || standardQ.isLoading}>
          Apply to this job only
        </Button>
        <Button variant="contained" onClick={(e) => setScopeAnchor(e.currentTarget)} disabled={saving || standardQ.isLoading}>
          Save as standard…
        </Button>
        <Menu anchorEl={scopeAnchor} open={!!scopeAnchor} onClose={() => setScopeAnchor(null)}>
          {entry.forme && (
            <MenuItem onClick={() => savePermanent('forme', entry.forme)}>For this forme · {entry.forme}</MenuItem>
          )}
          {entry.cust && (
            <MenuItem onClick={() => savePermanent('customer', entry.cust)}>For this customer · {entry.cust}</MenuItem>
          )}
          {entry.stockCode && (
            <MenuItem onClick={() => savePermanent('stock_code', entry.stockCode)}>For this paper · {entry.stockCode}</MenuItem>
          )}
        </Menu>
      </DialogActions>
    </Dialog>
  );
}
